import React, { useState, useEffect } from 'react';
import { ProjectStatus } from '../types';
import type { Project, ProjectUpdate, Task, BitumenSpec } from '../types';
import { X, Calendar, MapPin, Truck, DollarSign, User, Activity, Droplet, FileText, Clock, Sparkles, Loader2, Share2, Printer, ChevronRight, Plus, Send, CheckCircle2, Circle, Trash2, ShieldAlert, ArrowDown, ChevronDown, ChevronUp, Bot, Briefcase } from 'lucide-react';
import { generateProjectAnalysis, generateSmartUpdate, AIProjectReport } from '../services/geminiService';
import { mockEmployees } from '../mockData';

interface ProjectDetailsModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (project: Project) => Promise<void>;
}

export const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({ project, isOpen, onClose, onUpdate }) => {
  const [analysis, setAnalysis] = useState<AIProjectReport | null>(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [expandedSpecIdx, setExpandedSpecIdx] = useState<number | null>(null);
  
  // Update state
  const [newUpdateText, setNewUpdateText] = useState('');
  const [isAddingUpdate, setIsAddingUpdate] = useState(false);
  const [isRefiningUpdate, setIsRefiningUpdate] = useState(false);

  // Task state
  const [taskView, setTaskView] = useState<'list' | 'add'>('list');
  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    dueDate: new Date().toISOString().split('T')[0],
    priority: 'Medium',
    status: 'Pending',
    assigneeId: ''
  });

  useEffect(() => {
    if (isOpen) {
      setAnalysis(null);
      setIsLoadingAnalysis(false);
      setNewUpdateText('');
      setIsAddingUpdate(false);
      setTaskView('list');
      setExpandedSpecIdx(null);
    }
  }, [project, isOpen]);

  if (!isOpen || !project) return null;

  const handleAnalyze = async () => {
    setIsLoadingAnalysis(true);
    try {
      const result = await generateProjectAnalysis(project);
      setAnalysis(result);
    } catch (e) {
      console.error("AI Analysis failed", e);
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const handleApplyAISpec = async (suggested: { grade: string, quantity: number }) => {
    if (!onUpdate) return;
    
    const newSpec: BitumenSpec = {
      id: crypto.randomUUID(),
      grade: suggested.grade,
      quantity: suggested.quantity
    };

    const updatedProject = {
      ...project,
      bitumenSpecs: [...(project.bitumenSpecs || []), newSpec]
    };

    await onUpdate(updatedProject);
    if (analysis) {
        setAnalysis({
            ...analysis,
            suggestedSpecs: analysis.suggestedSpecs.filter(s => s.grade !== suggested.grade || s.quantity !== suggested.quantity)
        });
    }
  };

  const handleSmartRefineUpdate = async () => {
    if (!newUpdateText.trim()) return;
    setIsRefiningUpdate(true);
    try {
      const refined = await generateSmartUpdate(project, newUpdateText);
      setNewUpdateText(refined);
    } catch (error) {
      console.error("Smart refine failed", error);
    } finally {
      setIsRefiningUpdate(false);
    }
  };

  const handleAddUpdate = async () => {
    if (!newUpdateText.trim() || !onUpdate) return;
    
    setIsAddingUpdate(true);
    const update: ProjectUpdate = {
      id: crypto.randomUUID(),
      date: new Date().toISOString().split('T')[0],
      content: newUpdateText,
      author: 'System User'
    };
    
    const updatedProject = {
      ...project,
      updates: [...(project.updates || []), update]
    };

    try {
      await onUpdate(updatedProject);
      setNewUpdateText('');
    } catch (error) {
      console.error("Failed to add update", error);
    } finally {
      setIsAddingUpdate(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title || !onUpdate) return;

    const task: Task = {
      id: crypto.randomUUID(),
      projectId: project.id,
      title: newTask.title || '',
      dueDate: newTask.dueDate || new Date().toISOString().split('T')[0],
      priority: newTask.priority as any || 'Medium',
      status: 'Pending',
      assigneeId: newTask.assigneeId,
      country: project.country
    };

    const updatedProject = {
      ...project,
      tasks: [...(project.tasks || []), task]
    };

    try {
      await onUpdate(updatedProject);
      setNewTask({
        title: '',
        dueDate: new Date().toISOString().split('T')[0],
        priority: 'Medium',
        status: 'Pending',
        assigneeId: ''
      });
      setTaskView('list');
    } catch (error) {
      console.error("Failed to add task", error);
    }
  };

  const handleToggleTask = async (taskId: string) => {
    if (!onUpdate) return;

    const updatedTasks = (project.tasks || []).map(t => 
      t.id === taskId ? { ...t, status: t.status === 'Pending' ? 'Completed' : 'Pending' } as Task : t
    );

    await onUpdate({ ...project, tasks: updatedTasks });
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!onUpdate || !window.confirm('Delete this task?')) return;
    const updatedTasks = (project.tasks || []).filter(t => t.id !== taskId);
    await onUpdate({ ...project, tasks: updatedTasks });
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED: return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case ProjectStatus.IN_PROGRESS: return 'bg-blue-100 text-blue-800 border-blue-200';
      case ProjectStatus.PLANNING: return 'bg-amber-100 text-amber-800 border-amber-200';
      case ProjectStatus.UNDER_TENDERING: return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case ProjectStatus.PAUSED: return 'bg-rose-100 text-rose-800 border-rose-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getEmployee = (id?: string) => mockEmployees.find(e => e.id === id);

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[90vh] overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modern Header */}
        <div className="flex justify-between items-start p-8 border-b border-slate-100 bg-slate-50/50 shrink-0">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
               <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm ${getStatusColor(project.status)}`}>
                  {project.status}
               </span>
               <span className="text-slate-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <Clock size={14}/> 
                  Updated {project.updates.length > 0 ? project.updates[project.updates.length-1].date : 'Today'}
               </span>
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-2 leading-tight">{project.name}</h2>
            <div className="flex flex-wrap items-center gap-4 text-slate-500 text-sm font-medium">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-slate-200 shadow-sm">
                  <MapPin size={14} className="text-indigo-500"/> {project.region}
                </div>
                {project.tenderNumber && (
                   <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-slate-200 shadow-sm font-mono text-xs">
                    <FileText size={14} className="text-slate-400"/> {project.tenderNumber}
                  </div>
                )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="p-2.5 text-slate-400 hover:text-indigo-600 bg-white hover:bg-indigo-50 rounded-xl border border-slate-200 shadow-sm transition-all" title="Share Project">
              <Share2 size={20} />
            </button>
            <button onClick={() => window.print()} className="p-2.5 text-slate-400 hover:text-indigo-600 bg-white hover:bg-indigo-50 rounded-xl border border-slate-200 shadow-sm transition-all" title="Print Details">
              <Printer size={20} />
            </button>
            <div className="w-px h-10 bg-slate-200 mx-2"></div>
            <button onClick={onClose} className="p-2.5 text-slate-400 hover:text-rose-600 bg-white hover:bg-rose-50 rounded-xl border border-slate-200 shadow-sm transition-all">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-8 space-y-8 bg-white custom-scrollbar flex-grow">
            {/* Engineering Intelligence & Tasks Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Side: Core Metrics & Material Specs */}
                <div className="lg:col-span-8 space-y-8">
                    {/* Core Metrics Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                        <div className="p-6 bg-white rounded-3xl border border-slate-200 shadow-sm group hover:border-blue-400 transition-colors">
                            <div className="flex items-center gap-3 text-blue-600 mb-4">
                                <Activity size={24}/>
                                <span className="font-black text-[10px] uppercase tracking-widest">Construction Progress</span>
                            </div>
                            <div className="flex items-end justify-between">
                              <div className="text-4xl font-black text-slate-900">{project.progress}%</div>
                              <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                                  <div className="bg-blue-600 h-full rounded-full transition-all duration-1000" style={{width: `${project.progress}%`}}></div>
                              </div>
                            </div>
                        </div>

                        <div className="p-6 bg-white rounded-3xl border border-slate-200 shadow-sm group hover:border-purple-400 transition-colors">
                            <div className="flex items-center gap-3 text-purple-600 mb-4">
                                <MapPin size={24}/>
                                <span className="font-black text-[10px] uppercase tracking-widest">Total Distance</span>
                            </div>
                            <div className="text-4xl font-black text-slate-900">{project.kilometers} <span className="text-xs font-bold text-slate-400 uppercase">km</span></div>
                        </div>

                        <div className="p-6 bg-white rounded-3xl border border-slate-200 shadow-sm group hover:border-emerald-400 transition-colors">
                             <div className="flex items-center gap-3 text-emerald-600 mb-4">
                                <DollarSign size={24}/>
                                <span className="font-black text-[10px] uppercase tracking-widest">Funding Source</span>
                            </div>
                            <div className="text-lg font-black text-slate-900 truncate" title={project.financer}>{project.financer || 'N/A'}</div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Verified Agency</p>
                        </div>

                        <div className="p-6 bg-white rounded-3xl border border-slate-200 shadow-sm group hover:border-amber-400 transition-colors">
                             <div className="flex items-center gap-3 text-amber-600 mb-4">
                                <Briefcase size={24}/>
                                <span className="font-black text-[10px] uppercase tracking-widest">Consultant</span>
                            </div>
                            <div className="text-lg font-black text-slate-900 truncate" title={project.consultant}>{project.consultant || 'N/A'}</div>
                            <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Project Engineer</p>
                        </div>
                    </div>

                    {/* Bitumen Specifications Table */}
                    <div className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2 uppercase tracking-widest">
                                <Droplet size={20} className="text-indigo-500"/>
                                Project Specification Registry
                            </h3>
                            <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-black uppercase">{project.bitumenSpecs.length} Entries</span>
                            </div>
                        </div>
                        {project.bitumenSpecs && project.bitumenSpecs.length > 0 ? (
                            <div className="overflow-hidden rounded-2xl border border-slate-100">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead className="bg-slate-50/50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Grade Variation</th>
                                            <th className="px-6 py-4 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest">Required Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {project.bitumenSpecs.map((spec) => (
                                            <tr key={spec.id} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-700">{spec.grade}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-900 text-right">{spec.quantity.toLocaleString()} <span className="text-[10px] font-bold text-slate-400 uppercase">MT</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-50/50 font-black">
                                        <tr>
                                            <td className="px-6 py-4 text-sm text-slate-900">Aggregate Total</td>
                                            <td className="px-6 py-4 text-sm text-indigo-600 text-right">
                                                {project.bitumenSpecs.reduce((acc, curr) => acc + curr.quantity, 0).toLocaleString()} MT
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        ) : (
                            <div className="p-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-center">
                                <Droplet size={32} className="mx-auto text-slate-200 mb-3" />
                                <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">No Bitumen Specifications Defined</p>
                            </div>
                        )}
                    </div>

                    {/* AI Intelligence Report Section (Moved to bottom of column) */}
                    <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-[32px] p-1 overflow-hidden shadow-xl shadow-indigo-100">
                        <div className="bg-white/98 rounded-[28px] p-8">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-200 ring-4 ring-indigo-50">
                                       <Sparkles size={28}/>
                                    </div>
                                    <div>
                                      <h3 className="text-2xl font-black text-slate-900 tracking-tight">Intelligence Report</h3>
                                      <p className="text-indigo-500 text-[10px] font-black uppercase tracking-[0.2em]">Deep Scan: {project.region} Operations</p>
                                    </div>
                                </div>
                                {!analysis && (
                                    <button 
                                        onClick={handleAnalyze}
                                        disabled={isLoadingAnalysis}
                                        className="w-full md:w-auto px-8 py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.15em] shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
                                    >
                                        {isLoadingAnalysis ? <><Loader2 size={18} className="animate-spin"/> Scanning Network...</> : <><Sparkles size={18}/> Generate AI Report</>}
                                    </button>
                                )}
                            </div>
                            
                            {analysis ? (
                                 <div className="animate-in fade-in slide-in-from-top-4 duration-500 space-y-8">
                                    <div className="prose prose-sm max-w-none text-slate-600 bg-indigo-50/30 p-8 rounded-3xl border border-indigo-100 leading-relaxed italic text-base relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-4 opacity-5">
                                            <FileText size={80} />
                                        </div>
                                        <h4 className="not-italic font-black text-[10px] uppercase tracking-widest text-indigo-400 mb-4">Executive Summary</h4>
                                        {analysis.summary.split('\n').map((line, i) => <p key={i} className="mb-2 relative z-10">{line}</p>)}
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="font-black text-[10px] uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                                            <Droplet size={14} className="text-indigo-500" />
                                            AI-Optimized Material Requirements
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {analysis.suggestedSpecs.map((spec, idx) => (
                                                <div key={idx} className="bg-white border border-slate-200 rounded-[24px] p-5 hover:border-indigo-400 transition-all group shadow-sm">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div>
                                                            <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-black uppercase tracking-widest border border-amber-100">
                                                                {spec.grade}
                                                            </span>
                                                            <p className="text-2xl font-black text-slate-800 mt-2">{spec.quantity.toLocaleString()} <span className="text-xs text-slate-400">MT</span></p>
                                                        </div>
                                                        <button 
                                                            onClick={() => handleApplyAISpec(spec)}
                                                            className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                                                            title="Add to Project Specifications"
                                                        >
                                                            <Plus size={18} />
                                                        </button>
                                                    </div>
                                                    
                                                    <button 
                                                        onClick={() => setExpandedSpecIdx(expandedSpecIdx === idx ? null : idx)}
                                                        className="w-full flex items-center justify-between text-[10px] font-black uppercase text-slate-400 hover:text-indigo-500 py-2 border-t border-slate-50 mt-4 transition-colors"
                                                    >
                                                        Engineering Logic
                                                        {expandedSpecIdx === idx ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                    </button>
                                                    
                                                    {expandedSpecIdx === idx && (
                                                        <p className="text-xs text-slate-500 mt-3 leading-relaxed animate-in slide-in-from-top-1 duration-200">
                                                            {spec.reasoning}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {analysis.risks.map((risk, i) => (
                                            <div key={i} className="flex items-start gap-3 p-4 bg-rose-50 rounded-2xl border border-rose-100">
                                                <ShieldAlert size={16} className="text-rose-500 shrink-0 mt-0.5" />
                                                <span className="text-[11px] font-bold text-rose-700 leading-tight">{risk}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-center pt-4">
                                        <button onClick={() => setAnalysis(null)} className="px-6 py-2 rounded-full border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:bg-slate-50 transition-all">
                                            Close Intelligence Panel
                                        </button>
                                    </div>
                                 </div>
                            ) : !isLoadingAnalysis && (
                                <div className="bg-indigo-50/50 p-12 rounded-[28px] border-2 border-dashed border-indigo-200 text-center flex flex-col items-center">
                                    <Bot size={40} className="text-indigo-200 mb-4" />
                                    <p className="text-indigo-900 font-bold text-sm leading-relaxed max-w-lg">
                                        Activate Gemini 2.5 Intelligence to perform a context-aware analysis of your site updates and project parameters.
                                    </p>
                                    <p className="text-indigo-400 text-xs mt-2 uppercase tracking-widest font-black">Ready for deep engineering scan</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Side: Tasks & Updates */}
                <div className="lg:col-span-4 space-y-6">
                    {/* Project Tasks Section */}
                    <div className="bg-white p-6 rounded-[32px] border border-slate-200 shadow-sm flex flex-col min-h-[400px]">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2 uppercase tracking-widest">
                                <CheckCircle2 size={20} className="text-blue-500"/>
                                Project Tasks
                            </h3>
                            {taskView === 'list' ? (
                                <button 
                                    onClick={() => setTaskView('add')}
                                    className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all"
                                >
                                    <Plus size={18} />
                                </button>
                            ) : (
                                <button 
                                    onClick={() => setTaskView('list')}
                                    className="p-2 text-slate-400 hover:text-slate-600"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {taskView === 'add' ? (
                            <div className="space-y-4 animate-in slide-in-from-right-2 duration-300">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Task Description</label>
                                    <input 
                                        type="text"
                                        value={newTask.title}
                                        onChange={e => setNewTask({...newTask, title: e.target.value})}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., Deliver MC-30 to site"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Due Date</label>
                                        <input 
                                            type="date"
                                            value={newTask.dueDate}
                                            onChange={e => setNewTask({...newTask, dueDate: e.target.value})}
                                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Priority</label>
                                        <select 
                                            value={newTask.priority}
                                            onChange={e => setNewTask({...newTask, priority: e.target.value as any})}
                                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                                        >
                                            <option value="Low">Low Priority</option>
                                            <option value="Medium">Medium Priority</option>
                                            <option value="High">High Priority</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Assign to Employee</label>
                                    <select 
                                        value={newTask.assigneeId}
                                        onChange={e => setNewTask({...newTask, assigneeId: e.target.value})}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                                    >
                                        <option value="">-- No Assignee --</option>
                                        {mockEmployees.map(emp => (
                                            <option key={emp.id} value={emp.id}>{emp.name} ({emp.role})</option>
                                        ))}
                                    </select>
                                </div>
                                <button 
                                    onClick={handleAddTask}
                                    disabled={!newTask.title}
                                    className="w-full py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all disabled:opacity-50"
                                >
                                    Assign Task
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4 overflow-y-auto flex-grow max-h-[400px] pr-1">
                                {project.tasks && project.tasks.length > 0 ? (
                                    project.tasks.map(task => {
                                        const assignee = getEmployee(task.assigneeId);
                                        return (
                                            <div key={task.id} className={`p-4 rounded-2xl border transition-all flex items-start gap-4 ${task.status === 'Completed' ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-200 hover:border-blue-300'}`}>
                                                <button 
                                                    onClick={() => handleToggleTask(task.id)}
                                                    className={`mt-1 transition-colors ${task.status === 'Completed' ? 'text-emerald-500' : 'text-slate-300 hover:text-blue-500'}`}
                                                >
                                                    {task.status === 'Completed' ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                                                </button>
                                                <div className="flex-grow min-w-0">
                                                    <p className={`text-sm font-bold truncate ${task.status === 'Completed' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{task.title}</p>
                                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${task.priority === 'High' ? 'bg-rose-50 text-rose-600' : task.priority === 'Medium' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>
                                                            {task.priority}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                                                            <Clock size={10} /> {task.dueDate}
                                                        </span>
                                                    </div>
                                                    {assignee && (
                                                        <div className="mt-3 flex items-center gap-2 bg-slate-50 p-1.5 rounded-lg border border-slate-100 w-fit">
                                                            <img src={assignee.avatarUrl} className="w-5 h-5 rounded-full" alt="" />
                                                            <span className="text-[10px] font-bold text-slate-600 truncate max-w-[100px]">{assignee.name}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <button 
                                                    onClick={() => handleDeleteTask(task.id)}
                                                    className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-12">
                                        <CheckCircle2 size={32} className="opacity-20 mb-3" />
                                        <p className="text-xs font-medium">All clear! No tasks assigned.</p>
                                        <button onClick={() => setTaskView('add')} className="mt-4 text-blue-600 font-bold text-xs uppercase hover:underline">Add First Task</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Enhanced Site Update Section */}
                    <div className="bg-indigo-50 border border-indigo-100 p-6 rounded-[32px] shadow-inner">
                        <h3 className="text-sm font-black text-indigo-900 mb-4 flex items-center gap-2 uppercase tracking-widest">
                            <Plus size={16} className="text-indigo-600"/>
                            Post Site Update
                        </h3>
                        <div className="space-y-4">
                            <textarea 
                                value={newUpdateText}
                                onChange={(e) => setNewUpdateText(e.target.value)}
                                disabled={isAddingUpdate || isRefiningUpdate}
                                className="w-full p-4 bg-white border border-indigo-200 rounded-2xl text-xs focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none min-h-[120px] resize-none transition-all placeholder:text-slate-300"
                                placeholder="Report progress, challenges, or material needs..."
                            />
                            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                                <button 
                                    onClick={handleSmartRefineUpdate}
                                    disabled={!newUpdateText.trim() || isRefiningUpdate || isAddingUpdate}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-50 transition-all disabled:opacity-50 shadow-sm"
                                >
                                    {isRefiningUpdate ? (
                                        <><Loader2 size={14} className="animate-spin"/> Refining...</>
                                    ) : (
                                        <><Sparkles size={14}/> Refine with AI</>
                                    )}
                                </button>
                                
                                <button 
                                    onClick={handleAddUpdate}
                                    disabled={!newUpdateText.trim() || isAddingUpdate || isRefiningUpdate}
                                    className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 shadow-lg shadow-indigo-100 flex items-center justify-center gap-2"
                                >
                                    {isAddingUpdate ? (
                                        <><Loader2 size={14} className="animate-spin"/> Posting...</>
                                    ) : (
                                        <><Send size={14}/> Post Update</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Log */}
            <div className="bg-slate-50 p-8 rounded-[32px] border border-slate-200">
                <h3 className="text-lg font-black text-slate-800 mb-8 flex items-center gap-2 uppercase tracking-widest">
                    <Clock size={20} className="text-slate-400"/>
                    Project Activity Log
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {project.updates && project.updates.length > 0 ? (
                        [...project.updates].reverse().map((update) => (
                            <div key={update.id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                                <div className="flex justify-between items-center mb-3">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{update.date}</span>
                                    <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-lg uppercase">{update.author}</span>
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    {update.content}
                                </p>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full py-12 text-center text-slate-400">
                            <Clock size={32} className="mx-auto mb-2 opacity-20" />
                            <p className="text-sm font-medium">No updates recorded for this project.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
